import { useCallback, useEffect, useState } from 'react'
import { Download, ExternalLink, FileText, Loader2, Play } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { downloadFile } from '@/shared/api/client'
import { presignFile, type PresignedFile } from '@/features/geography/api'
import type { Evidence } from './api'

/**
 * Evidence you can actually look at — a photo that renders, audio that plays, video that
 * plays, a document you can open.
 *
 * ## Why the presigned URL is fetched, not linked
 *
 * `/files/{id}/download` answers with a 302 to the object store. A media element cannot
 * carry the bearer token, so pointing `<img src>` at it gets a 401 rather than a redirect.
 * `/files/{id}/url` hands back the presigned link as JSON; the client fetches that WITH the
 * token and then points the element at a link that needs no auth of its own.
 *
 * ## Why it is fetched on demand and not for the whole list
 *
 * The presign lasts five minutes. Signing every attachment when the tab opens means a
 * drawer left open over lunch shows a wall of expired media, and it signs URLs for files
 * nobody looked at. Each item presigns when it is first opened, and can be retried.
 *
 * ## Withdrawn evidence still opens
 *
 * A retraction is a new fact, not an erasure — the record keeps the row and the reason.
 * Somebody reviewing why an item was withdrawn has to be able to see what was withdrawn.
 */
export function EvidenceViewer({ row }: { row: Evidence }) {
  const [file, setFile] = useState<PresignedFile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    if (!row.fileId) return
    setLoading(true)
    setError(null)
    try {
      setFile(await presignFile(row.fileId))
    } catch (err) {
      setError(explain(err))
    } finally {
      setLoading(false)
    }
  }, [row.fileId])

  // Presign as soon as the item is opened, and re-presign if the row changes underneath.
  useEffect(() => { if (open && !file) void load() }, [open, file, load])

  // A link is not a stored file — there is nothing to presign, and the URL is the content.
  if (row.externalUrl) {
    return (
      <a href={row.externalUrl} target="_blank" rel="noreferrer noopener"
        className="inline-flex items-center gap-1.5 text-erp-xs text-chocolate hover:underline">
        <ExternalLink className="h-3 w-3" /> Open the link
      </a>
    )
  }

  /*
     A live feed lives in `coverage`, not in storage.

     Linked to the events LIST and not to the event itself, because `coverage` has no deep
     link: `EventsPage` holds the open event in local state rather than in the URL, so
     `/coverage/events/{id}` is not a route and would fall through the router's catch-all
     to the administration page. A link to the right list with the ref shown beats a link
     that silently lands somewhere else. If coverage ever moves that id into the URL, this
     becomes a one-line change.
  */
  if (row.coverageEventId) {
    return (
      <div className="mt-1">
        <a href="/coverage/events"
          className="inline-flex items-center gap-1.5 text-erp-xs text-chocolate hover:underline">
          <Play className="h-3 w-3" /> Open in Live Coverage
        </a>
        <span className="ml-2 font-mono text-erp-xs text-muted-foreground">
          event {row.coverageEventId.slice(0, 8)}
        </span>
      </div>
    )
  }

  if (!row.fileId) return null

  const kind = mediaKind(row.kindCode, file?.contentType)

  return (
    <div className="mt-1.5">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 text-erp-xs text-chocolate hover:underline">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ViewIcon kind={kind} />}
          {open ? 'Hide' : viewVerb(kind)}
        </button>

        {/* Always offered, whatever the kind. Somebody assembling a court bundle needs the
            original file, not a rendering of it. */}
        <button type="button"
          onClick={() => void downloadFile(`/files/${row.fileId}/download`,
            file?.filename ?? undefined)}
          className="inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground hover:text-foreground">
          <Download className="h-3 w-3" /> Download
        </button>

        {file?.sizeBytes != null && (
          <span className="text-erp-xs text-muted-foreground/70">{humanSize(file.sizeBytes)}</span>
        )}
      </div>

      {error && (
        <p className="mt-1 text-erp-xs text-red-700 dark:text-red-400">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="underline">Try again</button>
        </p>
      )}

      {open && file && (
        <div className="mt-2">
          {kind === 'image' && (
            <a href={file.url} target="_blank" rel="noreferrer noopener">
              <img src={file.url} alt={row.caption ?? 'Evidence photo'}
                className="max-h-80 rounded-md border border-border" />
            </a>
          )}
          {kind === 'audio' && (
            // `controls` and nothing else: no autoplay. Evidence that starts playing on its
            // own in an open-plan office is evidence people stop opening.
            <audio controls preload="metadata" src={file.url} className="w-full max-w-md" />
          )}
          {kind === 'video' && (
            <video controls preload="metadata" src={file.url}
              className="max-h-80 w-full max-w-lg rounded-md border border-border bg-black" />
          )}
          {kind === 'pdf' && (
            <iframe title={row.caption ?? 'Evidence document'} src={file.url}
              className="w-full h-96 rounded-md border border-border" />
          )}
          {kind === 'other' && (
            <p className="text-erp-xs text-muted-foreground">
              This file type has no in-page viewer
              {file.contentType ? ` (${file.contentType})` : ''}. Download it to open.
            </p>
          )}
          <p className="mt-1 text-erp-xs text-muted-foreground/70">
            This link expires {new Date(file.expiresAt).toLocaleTimeString()}. Reopen to
            refresh it.
          </p>
        </div>
      )}
    </div>
  )
}

type MediaKind = 'image' | 'audio' | 'video' | 'pdf' | 'other'

/**
 * What to render this as.
 *
 * The SERVED content type wins where there is one — it is what the object store will
 * actually send, and the evidence kind is a human's classification that can disagree
 * (a "SCREENSHOT" is an image; a "DOCUMENT" might be a PDF or a spreadsheet). The kind is
 * the fallback for the moment before the presign returns.
 */
function mediaKind(kindCode: string, contentType?: string | null): MediaKind {
  if (contentType) {
    if (contentType.startsWith('image/')) return 'image'
    if (contentType.startsWith('audio/')) return 'audio'
    if (contentType.startsWith('video/')) return 'video'
    if (contentType === 'application/pdf') return 'pdf'
    return 'other'
  }
  switch (kindCode) {
    case 'PHOTO':
    case 'SCREENSHOT': return 'image'
    case 'AUDIO': return 'audio'
    case 'VIDEO': return 'video'
    default: return 'other'
  }
}

function viewVerb(kind: MediaKind): string {
  switch (kind) {
    case 'image': return 'View the photo'
    case 'audio': return 'Listen'
    case 'video': return 'Watch'
    case 'pdf': return 'Read the document'
    default: return 'Open'
  }
}

function ViewIcon({ kind }: { kind: MediaKind }) {
  if (kind === 'audio' || kind === 'video') return <Play className="h-3 w-3" />
  return <FileText className="h-3 w-3" />
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
